import React, { useCallback, useEffect, useState } from "react";
import {
  Instagram,
  Linkedin,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  UserRound,
} from "lucide-react";
import CMSLayout from "@/components/cms/CMSLayout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
  createTeamMember,
  deleteTeamMember,
  listTeamMembers,
  updateTeamMember,
} from "@/lib/teamMembersApi";
import { uploadMedia } from "@/lib/mediaApi";
import { fieldClass, labelClass } from "@/components/cms/formStyles";

const emptyForm = {
  name: "",
  position: "",
  speciality: "",
  bio: "",
  image_url: "",
  linkedin: "",
  instagram: "",
  email: "",
  is_active: true,
  order: 0,
};

const memberToForm = (member) => ({
  name: member.name || "",
  position: member.position || "",
  speciality: member.speciality || "",
  bio: member.bio || "",
  image_url: member.image_url || "",
  linkedin: member.social_links?.linkedin || "",
  instagram: member.social_links?.instagram || "",
  email: member.social_links?.email || "",
  is_active: Boolean(member.is_active),
  order: member.order ?? 0,
});

const formToPayload = (form) => ({
  name: form.name,
  position: form.position || null,
  speciality: form.speciality || null,
  bio: form.bio || null,
  image_url: form.image_url || null,
  social_links: {
    linkedin: form.linkedin || "",
    instagram: form.instagram || "",
    email: form.email || "",
  },
  is_active: form.is_active,
  order: Number(form.order) || 0,
});

const TeamManagementPage = () => {
  const [members, setMembers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [brokenImages, setBrokenImages] = useState(() => new Set());
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const fetchMembers = useCallback(async (searchTerm, pageNum) => {
    setLoading(true);
    setError("");
    try {
      const result = await listTeamMembers({ search: searchTerm, page: pageNum });
      setMembers(result.members);
      setPagination(result.pagination);
    } catch (err) {
      setError("Gagal memuat data tim. Periksa koneksi ke server.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchMembers(search, page);
    }, 350);
    return () => clearTimeout(timeout);
  }, [search, page, fetchMembers]);

  useEffect(() => {
    if (!notice) return;
    const timeout = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(timeout);
  }, [notice]);

  const openCreateForm = () => {
    setEditingMember(null);
    setForm(emptyForm);
    setFormError("");
    setUploadError("");
    setIsFormOpen(true);
  };

  const openEditForm = (member) => {
    setEditingMember(member);
    setForm(memberToForm(member));
    setFormError("");
    setUploadError("");
    setIsFormOpen(true);
  };

  const handleFormChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadError("");
    setUploadingImage(true);
    try {
      const media = await uploadMedia(file);
      setForm((prev) => ({ ...prev, image_url: media.url }));
    } catch (err) {
      setUploadError(
        err.response?.data?.errors?.file?.[0] || "Gagal mengunggah foto. Coba lagi."
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError("Nama wajib diisi.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = formToPayload(form);
      if (editingMember) {
        await updateTeamMember(editingMember.id, payload);
        setNotice({ type: "success", message: "Anggota tim berhasil diperbarui." });
      } else {
        await createTeamMember(payload);
        setNotice({ type: "success", message: "Anggota tim berhasil ditambahkan." });
      }
      setIsFormOpen(false);
      fetchMembers(search, page);
    } catch (err) {
      setFormError(err.response?.data?.message || "Gagal menyimpan data. Coba lagi.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTeamMember(deleteTarget.id);
      setNotice({ type: "success", message: "Anggota tim berhasil dihapus." });
      setDeleteTarget(null);
      const isLastItemOnPage = members.length === 1 && page > 1;
      fetchMembers(search, isLastItemOnPage ? page - 1 : page);
      if (isLastItemOnPage) setPage(page - 1);
    } catch (err) {
      setNotice({ type: "error", message: "Gagal menghapus anggota tim." });
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (member) => {
    try {
      await updateTeamMember(member.id, { is_active: !member.is_active });
      setMembers((prev) =>
        prev.map((m) => (m.id === member.id ? { ...m, is_active: !m.is_active } : m))
      );
    } catch (err) {
      setNotice({ type: "error", message: "Gagal mengubah status anggota tim." });
    }
  };

  return (
    <CMSLayout
      active="Tim"
      breadcrumb="CMS / Tim"
      title="Manajemen Tim"
      headerActions={
        <>
          <div className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-300">
            <Search size={16} />
            <input
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Cari nama, posisi..."
              className="w-40 bg-transparent text-slate-100 placeholder:text-slate-500 focus:outline-none"
            />
          </div>
          <button
            onClick={openCreateForm}
            className="flex items-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400"
          >
            <Plus size={16} /> Tambah Anggota
          </button>
        </>
      }
    >
      {notice && (
        <div
          className={`rounded-2xl px-4 py-3 text-sm ${
            notice.type === "success"
              ? "bg-emerald-500/10 text-emerald-300"
              : "bg-red-500/10 text-red-400"
          }`}
        >
          {notice.message}
        </div>
      )}

      <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400">Struktur organisasi</p>
            <h2 className="text-xl font-semibold text-white">
              {pagination ? `${pagination.total} Anggota Tim` : "Daftar Tim"}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 flex items-center justify-center gap-2 py-12 text-slate-400">
            <Loader2 size={18} className="animate-spin" /> Memuat data...
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl bg-red-500/10 p-4 text-sm text-red-400">{error}</div>
        ) : members.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-700 p-8 text-center text-sm text-slate-400">
            Belum ada anggota tim. Klik "Tambah Anggota" untuk menambahkan.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-950/70 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-800 text-slate-500">
                    {member.image_url && !brokenImages.has(member.id) ? (
                      <img
                        src={member.image_url}
                        alt={member.name}
                        className="h-full w-full object-cover"
                        onError={() =>
                          setBrokenImages((prev) => new Set(prev).add(member.id))
                        }
                      />
                    ) : (
                      <UserRound size={22} />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">{member.name}</p>
                    <p className="text-sm text-slate-400">
                      {member.position || "-"}
                      {member.speciality ? ` • ${member.speciality}` : ""}
                    </p>
                    <div className="mt-1.5 flex items-center gap-2 text-slate-500">
                      {member.social_links?.linkedin && (
                        <Linkedin size={14} />
                      )}
                      {member.social_links?.instagram && (
                        <Instagram size={14} />
                      )}
                      {member.social_links?.email && <Mail size={14} />}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 sm:justify-end">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={member.is_active}
                      onCheckedChange={() => toggleActive(member)}
                      className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-700"
                    />
                    <span className="text-xs text-slate-400">
                      {member.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </div>
                  <button
                    onClick={() => openEditForm(member)}
                    className="rounded-full p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(member)}
                    className="rounded-full p-2 text-slate-400 transition hover:bg-red-500/10 hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination && pagination.total_pages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm text-slate-400">
            <span>
              Halaman {pagination.current_page} dari {pagination.total_pages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-full border border-slate-700 px-3 py-1.5 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Sebelumnya
              </button>
              <button
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                className="rounded-full border border-slate-700 px-3 py-1.5 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Berikutnya
              </button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingMember ? "Edit Anggota Tim" : "Tambah Anggota Tim"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Isi data anggota tim untuk ditampilkan di halaman website.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Nama *</label>
                <input
                  className={fieldClass}
                  value={form.name}
                  onChange={handleFormChange("name")}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Posisi</label>
                <input
                  className={fieldClass}
                  value={form.position}
                  onChange={handleFormChange("position")}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Speciality</label>
                <input
                  className={fieldClass}
                  value={form.speciality}
                  onChange={handleFormChange("speciality")}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Foto</label>
              <div className="flex items-center gap-3">
                {form.image_url && (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-700 bg-slate-800 text-slate-500">
                    <img
                      key={form.image_url}
                      src={form.image_url}
                      alt="Preview"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    className={fieldClass}
                    value={form.image_url}
                    onChange={handleFormChange("image_url")}
                    placeholder="https://... atau unggah file di bawah"
                  />
                  <label
                    className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-300 transition hover:bg-slate-800 ${
                      uploadingImage ? "pointer-events-none opacity-60" : ""
                    }`}
                  >
                    {uploadingImage ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    Unggah Foto
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              </div>
              {uploadError && (
                <p className="mt-1.5 text-xs text-red-400">{uploadError}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Bio</label>
              <textarea
                className={`${fieldClass} min-h-[80px]`}
                value={form.bio}
                onChange={handleFormChange("bio")}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className={labelClass}>LinkedIn</label>
                <input
                  className={fieldClass}
                  value={form.linkedin}
                  onChange={handleFormChange("linkedin")}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className={labelClass}>Instagram</label>
                <input
                  className={fieldClass}
                  value={form.instagram}
                  onChange={handleFormChange("instagram")}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  className={fieldClass}
                  value={form.email}
                  onChange={handleFormChange("email")}
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div>
                <label className={labelClass}>Urutan</label>
                <input
                  type="number"
                  className={`${fieldClass} w-24`}
                  value={form.order}
                  onChange={handleFormChange("order")}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_active: checked }))}
                  className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-700"
                />
                <span className="text-sm text-slate-300">Aktif ditampilkan</span>
              </div>
            </div>

            {formError && (
              <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-400">{formError}</p>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:bg-slate-800"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center justify-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                Simpan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Hapus anggota tim?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Tindakan ini akan menghapus {deleteTarget?.name} secara permanen dan tidak dapat
              dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 text-white hover:bg-red-400"
            >
              {deleting ? "Menghapus..." : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CMSLayout>
  );
};

export default TeamManagementPage;
